import React from "react";
import { Typography, Box, Button, Paper } from "@mui/material";
import DataTable from "./viewers/DataTable";
import Visualization from "./viewers/Visualization";
import { exportRowsToCsv } from "../utils/fileHelpers";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import DataObjectIcon from "@mui/icons-material/DataObject";
import BarChartIcon from "@mui/icons-material/BarChart";

export default function RightPanel({ rows, schema, darkMode }) {
  // Check if rows exist
  const isDataAvailable = Array.isArray(rows) && rows.length > 0;

  // Check if schema is valid
  const hasSchema =
    schema && Array.isArray(schema.columns) && schema.columns.length > 0;

  return (
    <Box
      sx={{
        flex: 1,
        overflowY: "auto",
        p: 2,
        bgcolor: darkMode ? "#1e293b" : "#f0f4f8",
        color: darkMode ? "#e2e8f0" : "#1e293b",
        transition: "background-color 0.3s ease",
      }}
    >
      {/* Tools & Export */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Data Preview
        </Typography>

        {/* Export button only if rows exist */}
        {isDataAvailable && (
          <Button
            variant="contained"
            onClick={() => exportRowsToCsv(rows, "asksam_export.csv")}
            startIcon={<CloudDownloadIcon />}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              bgcolor: "#0b74ff",
              "&:hover": { bgcolor: "#0866d9" },
            }}
          >
            Export CSV
          </Button>
        )}
      </Box>

      {/* No Data State */}
      {!isDataAvailable && (
        <Paper
          elevation={4}
          sx={{
            textAlign: "center",
            borderRadius: "12px",
            bgcolor: darkMode ? "#2d384b" : "#ffffff",
            p: 3,
          }}
        >
          <DataObjectIcon
            sx={{
              fontSize: 60,
              color: darkMode ? "#64748b" : "#94a3b8",
              mb: 1,
            }}
          />
          <Typography
            variant="body1"
            sx={{ color: darkMode ? "#94a3b8" : "#64748b" }}
          >
            Upload a file to view data and insights here.
          </Typography>
        </Paper>
      )}

      {/* Schema Section */}
      {isDataAvailable && hasSchema && (
        <Paper
          elevation={4}
          sx={{
            p: 2,
            mt: 2,
            borderRadius: "12px",
            bgcolor: darkMode ? "#2d384b" : "#ffffff",
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              mb: 1,
              color: darkMode ? "#a5b4fc" : "#2563eb",
            }}
          >
            Detected Schema
          </Typography>
          <Box>
            {schema.columns.map((c, i) => (
              <Box
                key={i}
                sx={{
                  borderBottom: `1px solid ${
                    darkMode ? "rgba(255,255,255,0.08)" : "#eee"
                  }`,
                  py: 1,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ display: "flex", justifyContent: "space-between" }}
                >
                  <strong>{c.name}</strong>
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{
                      color: darkMode ? "#94a3b8" : "#64748b",
                      fontStyle: "italic",
                    }}
                  >
                    {c.detectedType}
                  </Typography>
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      {/* Visualizations */}
      {isDataAvailable && (
        <Paper
          elevation={4}
          sx={{
            p: 2,
            mt: 3,
            borderRadius: "12px",
            bgcolor: darkMode ? "#2d384b" : "#ffffff",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <BarChartIcon sx={{ color: darkMode ? "#a5b4fc" : "#2563eb" }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Visualizations
            </Typography>
          </Box>
          <Visualization
            rows={rows}
            schema={hasSchema ? schema : null}
            darkMode={darkMode}
          />
        </Paper>
      )}

      {/* Data Table */}
      {isDataAvailable && (
        <Paper
          elevation={4}
          sx={{
            p: 2,
            mt: 3,
            borderRadius: "12px",
            bgcolor: darkMode ? "#2d384b" : "#ffffff",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <DataObjectIcon sx={{ color: darkMode ? "#a5b4fc" : "#2563eb" }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Data Table (Top 100 rows)
            </Typography>
          </Box>
          <DataTable
            rows={rows?.slice(0, 100) || []}
            schema={hasSchema ? schema : null}
            darkMode={darkMode}
          />
        </Paper>
      )}
    </Box>
  );
}
